import * as React from "react"
import { cn } from "@/lib/utils"

interface SplitViewProps {
  editPanel: React.ReactNode;
  previewPanel: React.ReactNode;
  className?: string;
  orientation?: 'horizontal' | 'vertical'; // Future: support vertical split
  minHeight?: string; // Minimum height constraint
  maxHeight?: string; // Maximum height constraint
}

const SplitView = React.forwardRef<
  HTMLDivElement,
  SplitViewProps
>(({ editPanel, previewPanel, className, orientation = 'horizontal', minHeight, maxHeight }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "split-view",
        // Base layout
        "flex w-full",
        // Orientation-specific layout
        orientation === 'horizontal' 
          ? "flex-col md:flex-row" // Stack vertically on mobile, side-by-side on desktop
          : "flex-col", // Always vertical (for future use)
        // Styling - use gray-300 for lighter borders
        "gap-0 border border-gray-300",
        // Height constraints - ensure enough height for proper splitting
        minHeight && minHeight,
        maxHeight && maxHeight,
        // Add minimum height for mobile split view to work properly
        !minHeight && "min-h-[600px]", // Default minimum height if not provided
        maxHeight && "overflow-hidden", // Prevent container overflow when maxHeight is set
        className
      )}
      id="markdown-content"
    >
      {/* Edit Panel */}
      <div 
        className={cn(
          "split-view-edit",
          // Base sizing and flex layout
          "flex flex-col flex-1", // Equal distribution
          // Minimum height to ensure edit panel is always usable
          "min-h-[300px]", // Minimum usable height for editing
          // Responsive sizing
          orientation === 'horizontal' 
            ? "w-full md:w-1/2" 
            : "w-full",
          // Styling - remove border to prevent double-border with divider
          "border-0",
          "bg-background",
          // Add padding for mobile separation
          "pb-3 md:pb-0", // Bottom padding on mobile only
          // Height constraints for consistent behavior
          "overflow-y-auto"
        )}
      >
        {editPanel}
      </div>

      {/* Divider */}
      <div 
        className={cn(
          "split-view-divider",
          orientation === 'horizontal'
            ? "h-[2px] md:h-auto md:w-px bg-gray-400 mx-4 md:mx-0" // Thicker horizontal divider with margin on mobile, vertical on desktop
            : "h-[2px] bg-gray-400" // Thicker horizontal divider for vertical split
        )}
      />

      {/* Preview Panel */}
      <div 
        className={cn(
          "split-view-preview",
          // Base sizing and flex layout
          "flex flex-col flex-1", // Always flex-1 for equal distribution
          // Responsive sizing
          orientation === 'horizontal' 
            ? "w-full md:w-1/2" 
            : "w-full",
          // Styling - use gray-300 for lighter borders
          "border-0",
          "bg-background",
          // Add padding for mobile separation
          "pt-3 md:pt-0", // Top padding on mobile only
          // Height constraints for consistent behavior
          "overflow-y-auto"
        )}
      >
        {previewPanel}
      </div>
    </div>
  );
});

SplitView.displayName = "SplitView";

export { SplitView }; 